import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm, Link } from '@inertiajs/react';
import InputError from '@/Components/InputError';
import toast from 'react-hot-toast';

export default function RegisterModal({ isOpen, closeModal, openLogin }) {
    const { data, setData, post, processing, errors, reset } = useForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
    });

    useEffect(() => {
        return () => {
            reset('password', 'password_confirmation');
        };
    }, []);

    const submit = (e) => {
        e.preventDefault();

        post(route('register'), {
            onSuccess: () => {
                closeModal();
                toast.success('Account created! Welcome aboard 🎉');
            },
            onFinish: () => reset('password', 'password_confirmation'),
        });
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal || (() => {})}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-3xl bg-background-dark text-left align-middle shadow-2xl transition-all border border-white/10 flex flex-col md:flex-row relative">

                                {/* Close Button */}
                                <button onClick={closeModal} className="absolute top-4 right-4 z-20 p-2 text-white/50 hover:text-white transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>

                                {/* Left Side: Image/Quote */}
                                <div className="hidden md:block md:w-1/2 relative min-h-[700px]">
                                    <div
                                        className="absolute inset-0 bg-cover bg-center"
                                        style={{ backgroundImage: "url('https://lh3.googleusercontent.com/aida-public/AB6AXuBuWBeNWHyxaDcirTXGebQSsUk6b1lcpmNZ-OFfL0w6ckS6HmoDkVhDnQcVhlFFNDdjlo9otM6aL9h96mz_ZynoMKa7p_u3BrkjBbeiDWo1iQSloQyRHLB6Z-DhYcySaM_eKgTsFwVruuq7R6g4KWzpXC6eUvuQ9qX28Ir4PtYQ403EJWidru8tHnAnRc_Fo_zREEIf_6AEEUj3vGsQprGu-dKbyoWGwuastmMbHGRc1YkMNSWYcJjARoHcduHI-dOClWLtfExYufCn')" }}
                                    ></div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-transparent to-transparent opacity-80"></div>
                                    <div className="absolute bottom-0 left-0 p-12 text-white">
                                        <p className="font-display italic text-3xl mb-4 text-primary drop-shadow-md">"Join the revolution."</p>
                                        <p className="text-sm text-white/90 font-light max-w-sm">Create your account to start generating stunning AI imagery today.</p>
                                    </div>
                                </div>

                                {/* Right Side: Register Form */}
                                <div className="w-full md:w-1/2 p-10 lg:p-16 flex flex-col justify-center bg-background-dark">
                                    <div className="w-full">
                                        <div className="mb-10">
                                            <h1 className="text-4xl font-display font-medium text-white mb-3">Sign Up</h1>
                                            <p className="text-white/50 text-sm md:text-base font-light">Join us and unleash your creativity.</p>
                                        </div>

                                        {/* Social Links Placeholder */}
                                        <div className="grid grid-cols-3 gap-3 mb-8">
                                            <a href={route('auth.social.redirect', 'google')} className="flex items-center justify-center gap-2 w-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-primary/30 text-white font-medium h-12 rounded-xl transition-all duration-200 group">
                                                <img alt="Google" className="w-5 h-5 group-hover:scale-110 transition-transform" src="https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA" />
                                            </a>
                                            <a href={route('auth.social.redirect', 'facebook')} className="flex items-center justify-center gap-2 w-full bg-[#1877F2]/10 border border-[#1877F2]/20 hover:bg-[#1877F2]/20 hover:border-[#1877F2]/40 text-white font-medium h-12 rounded-xl transition-all duration-200 group">
                                                <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                                </svg>
                                            </a>
                                            <a href={route('auth.social.redirect', 'twitter')} className="flex items-center justify-center gap-2 w-full bg-black/40 border border-white/10 hover:bg-black/60 hover:border-white/30 text-white font-medium h-12 rounded-xl transition-all duration-200 group">
                                                {/* X icon */}
                                                <svg className="w-5 h-5 text-white fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                                            </a>
                                        </div>

                                        <div className="relative flex py-2 items-center mb-8">
                                            <div className="flex-grow border-t border-white/10"></div>
                                            <span className="flex-shrink-0 mx-4 text-[10px] font-bold text-primary uppercase tracking-[0.2em]">OR</span>
                                            <div className="flex-grow border-t border-white/10"></div>
                                        </div>

                                        <form onSubmit={submit} className="space-y-6">
                                            <div className="space-y-1.5">
                                                <label className="block text-xs font-bold text-primary uppercase tracking-wider" htmlFor="name">Full Name</label>
                                                <div className="relative">
                                                    <input
                                                        className="block w-full h-12 rounded-xl border border-input-border bg-input-bg text-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm pl-12 transition-all outline-none placeholder:text-white/30"
                                                        id="name"
                                                        type="text"
                                                        name="name"
                                                        value={data.name}
                                                        onChange={(e) => setData('name', e.target.value)}
                                                        placeholder="John Doe"
                                                        required
                                                    />
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-white/50">
                                                        <span className="material-symbols-outlined text-[20px]">person</span>
                                                    </div>
                                                </div>
                                                <InputError message={errors.name} className="mt-2" />
                                            </div>

                                            <div className="space-y-1.5">
                                                <label className="block text-xs font-bold text-primary uppercase tracking-wider" htmlFor="email">Email Address</label>
                                                <div className="relative">
                                                    <input
                                                        className="block w-full h-12 rounded-xl border border-input-border bg-input-bg text-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm pl-12 transition-all outline-none placeholder:text-white/30"
                                                        id="email"
                                                        type="email"
                                                        name="email"
                                                        value={data.email}
                                                        onChange={(e) => setData('email', e.target.value)}
                                                        placeholder="name@example.com"
                                                        required
                                                    />
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-white/50">
                                                        <span className="material-symbols-outlined text-[20px]">mail</span>
                                                    </div>
                                                </div>
                                                <InputError message={errors.email} className="mt-2" />
                                            </div>

                                            <div className="space-y-1.5">
                                                <label className="block text-xs font-bold text-primary uppercase tracking-wider" htmlFor="password">Password</label>
                                                <div className="relative">
                                                    <input
                                                        className="block w-full h-12 rounded-xl border border-input-border bg-input-bg text-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm pl-12 transition-all outline-none placeholder:text-white/30"
                                                        id="password"
                                                        type="password"
                                                        name="password"
                                                        value={data.password}
                                                        onChange={(e) => setData('password', e.target.value)}
                                                        placeholder="••••••••"
                                                        required
                                                    />
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-white/50">
                                                        <span className="material-symbols-outlined text-[20px]">lock</span>
                                                    </div>
                                                </div>
                                                <InputError message={errors.password} className="mt-2" />
                                            </div>

                                            <div className="space-y-1.5">
                                                <label className="block text-xs font-bold text-primary uppercase tracking-wider" htmlFor="password_confirmation">Confirm Password</label>
                                                <div className="relative">
                                                    <input
                                                        className="block w-full h-12 rounded-xl border border-input-border bg-input-bg text-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm pl-12 transition-all outline-none placeholder:text-white/30"
                                                        id="password_confirmation"
                                                        type="password"
                                                        name="password_confirmation"
                                                        value={data.password_confirmation}
                                                        onChange={(e) => setData('password_confirmation', e.target.value)}
                                                        placeholder="••••••••"
                                                        required
                                                    />
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-white/50">
                                                        <span className="material-symbols-outlined text-[20px]">lock_reset</span>
                                                    </div>
                                                </div>
                                                <InputError message={errors.password_confirmation} className="mt-2" />
                                            </div>

                                            <button
                                                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-xl text-sm font-bold text-black bg-primary hover:bg-primary/90 focus:outline-none transition-all duration-300 mt-4 uppercase tracking-widest disabled:opacity-50"
                                                type="submit"
                                                disabled={processing}
                                            >
                                                Create Account
                                            </button>
                                        </form>

                                        <div className="mt-8 text-center">
                                            <p className="text-sm text-white/50 font-light">
                                                Already have an account?
                                                <button onClick={openLogin} className="font-bold text-white hover:text-primary transition-colors ml-2">Log in</button>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
